import { Calendar, Database, Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { handleError } from "../../lib/errors";
import { notify } from "../../lib/notify";
import { Backup, BackupSchedule } from "../../types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Skeleton } from "../ui/skeleton";
import { Switch } from "../ui/switch";
import { BackupRow } from "./BackupRow";
import { ScheduleForm } from "./ScheduleForm";

interface BackupsPanelProps {
  serviceId: string;
  serviceType?: string;
}

export function BackupsPanel({ serviceId, serviceType }: BackupsPanelProps) {
  const isDatabaseService = serviceType?.startsWith("database-") ?? false;

  const [backups, setBackups] = useState<Backup[]>([]);
  const [schedules, setSchedules] = useState<BackupSchedule[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [triggeringDb, setTriggeringDb] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [pendingDeleteSchedule, setPendingDeleteSchedule] = useState<BackupSchedule | null>(null);

  const loadBackups = useCallback(async (p = 1) => {
    try {
      const res = await api.backups.list(serviceId, p, 20);
      setBackups(res.backups ?? []);
      setTotal(res.pagination?.total ?? 0);
      setPage(p);
    } catch (err) {
      handleError(err, { scope: "BackupsPanel" });
    }
  }, [serviceId]);

  const loadSchedules = useCallback(async () => {
    try {
      const res = await api.backups.schedules.list(serviceId);
      setSchedules(res.schedules ?? []);
    } catch (err) {
      handleError(err, { scope: "BackupsPanel" });
    }
  }, [serviceId]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadBackups(1), loadSchedules()]);
      setLoading(false);
    })();
  }, [loadBackups, loadSchedules]);

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      const res = await api.backups.trigger(serviceId, "config");
      notify.success("Config backup created");
      setBackups((prev) => [res.backup, ...prev]);
      setTotal((t) => t + 1);
    } catch (err) {
      handleError(err, { scope: "BackupsPanel" });
    } finally {
      setTriggering(false);
    }
  };

  const handleTriggerDb = async () => {
    setTriggeringDb(true);
    try {
      const res = await api.backups.trigger(serviceId, "database");
      notify.success("Database backup started", { description: "Dump running in the background." });
      setBackups((prev) => [res.backup, ...prev]);
      setTotal((t) => t + 1);
    } catch (err) {
      handleError(err, { scope: "BackupsPanel" });
    } finally {
      setTriggeringDb(false);
    }
  };

  const handleBackupDeleted = (id: string) => {
    setBackups((prev) => prev.filter((b) => b.id !== id));
    setTotal((t) => Math.max(0, t - 1));
  };

  const handleScheduleCreated = (schedule: BackupSchedule) => {
    setSchedules((prev) => [schedule, ...prev]);
    setShowScheduleForm(false);
  };

  const handleToggleSchedule = async (sched: BackupSchedule) => {
    try {
      const res = await api.backups.schedules.update(serviceId, sched.id, {
        enabled: !sched.enabled,
      });
      setSchedules((prev) => prev.map((s) => (s.id === sched.id ? res.schedule : s)));
    } catch (err) {
      handleError(err, { scope: "BackupsPanel" });
    }
  };

  const handleDeleteSchedule = async () => {
    if (!pendingDeleteSchedule) return;
    const sched = pendingDeleteSchedule;
    setPendingDeleteSchedule(null);
    try {
      await api.backups.schedules.delete(serviceId, sched.id);
      notify.success("Schedule deleted");
      setSchedules((prev) => prev.filter((s) => s.id !== sched.id));
    } catch (err) {
      handleError(err, { scope: "BackupsPanel" });
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 pt-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-4">
      {/* Manual backup */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Config Backups</CardTitle>
              <CardDescription>
                Snapshot your service configuration — env vars (redacted), port, replicas, build
                commands, and repository settings.
              </CardDescription>
            </div>
            <Button onClick={handleTrigger} disabled={triggering} size="sm">
              {triggering ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              {triggering ? "Running…" : "Backup now"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {backups.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No backups yet. Click "Backup now" to create one.
            </p>
          ) : (
            <>
              {backups.map((b) => (
                <BackupRow key={b.id} backup={b} onDeleted={handleBackupDeleted} />
              ))}
              {total > backups.length && (
                <div className="flex justify-center pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadBackups(page + 1)}
                  >
                    <RefreshCw className="mr-2 h-3.5 w-3.5" />
                    Load more ({total - backups.length} remaining)
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Schedules */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Backup Schedules
              </CardTitle>
              <CardDescription>Automatically back up on a cron schedule.</CardDescription>
            </div>
            {!showScheduleForm && (
              <Button variant="outline" size="sm" onClick={() => setShowScheduleForm(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add schedule
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {showScheduleForm && (
            <ScheduleForm
              serviceId={serviceId}
              onCreated={handleScheduleCreated}
              onCancel={() => setShowScheduleForm(false)}
            />
          )}

          {schedules.length === 0 && !showScheduleForm ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No schedules configured.
            </p>
          ) : (
            schedules.map((sched) => (
              <div
                key={sched.id}
                className="flex items-center justify-between rounded-md bg-card p-3 text-sm"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">
                      {sched.cronExpression}
                    </code>
                    <Badge variant="outline" className="text-xs capitalize">
                      {sched.type}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      Keep {sched.retentionDays}d
                    </span>
                  </div>
                  {sched.lastRunAt && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Last run: {new Date(sched.lastRunAt).toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-2 shrink-0">
                  <Switch
                    checked={sched.enabled}
                    onCheckedChange={() => handleToggleSchedule(sched)}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => setPendingDeleteSchedule(sched)}
                    title="Delete schedule"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Database backup — shown only for managed database services */}
      {isDatabaseService && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  Database Backup
                </CardTitle>
                <CardDescription>
                  Run a full database dump (pg_dump / mongodump / mysqldump) inside the service pod.
                  The dump runs asynchronously — the backup status will update when complete.
                </CardDescription>
              </div>
              <Button onClick={handleTriggerDb} disabled={triggeringDb} size="sm" variant="outline">
                {triggeringDb ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Database className="mr-2 h-4 w-4" />
                )}
                {triggeringDb ? "Starting…" : "Dump now"}
              </Button>
            </div>
          </CardHeader>
        </Card>
      )}

      <p className="text-xs text-muted-foreground text-center">
        Volume backups coming in Phase 3.
      </p>

      {/* Schedule delete confirmation */}
      <AlertDialog
        open={!!pendingDeleteSchedule}
        onOpenChange={(open) => { if (!open) setPendingDeleteSchedule(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete schedule?</AlertDialogTitle>
            <AlertDialogDescription>
              The schedule{" "}
              <code className="font-mono text-xs">{pendingDeleteSchedule?.cronExpression}</code>{" "}
              will be permanently deleted. Existing backups are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSchedule}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
